/**
 * @module-isolated analytics-lab
 * Atención ahora — reusa alertas del resumen ejecutivo + cola leads (lectura).
 * No redefine KPIs oficiales; solo prioriza acciones.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import {
  AlertList,
  ErrorState,
  LoadingState,
  ModuleFilters,
  PageHeader,
  SectionHeader
} from '../../../shared/components/index.js';
import { useModuleFilters } from '../../../shared/hooks/useModuleFilters.js';
import { loadExecutiveSummary } from '../../resumen-ejecutivo/services/executiveSummaryService.js';
import { useLeadsData } from '../../leads/hooks/useLeadsData.jsx';
import { cacheGet } from '../../../shared/utils/session-cache.js';
import { useAnalyticsRefresh } from '../../../shared/hooks/useAnalyticsRefresh.js';
import LabBanner from '../components/LabBanner.jsx';

function cacheKey(dateFrom, dateTo) {
  return 'execSummary:' + String(dateFrom || '') + '|' + String(dateTo || '');
}

function formatLeadRow(lead) {
  if (!lead || typeof lead !== 'object') return null;
  const name = lead.name || lead.nombre || lead.title || 'Lead';
  const days =
    lead.ageDaysSinceCreation != null
      ? lead.ageDaysSinceCreation
      : lead.daysWithoutContact != null
        ? lead.daysWithoutContact
        : lead.ageDays != null
          ? lead.ageDays
          : null;
  const reasonParts = [lead.ciclo, lead.internalStateName || lead.stageName].filter(Boolean);
  return {
    name: String(name),
    days,
    reason: reasonParts.join(' · ')
  };
}

export default function LabAtencionPage() {
  const { email } = useOutletContext() || {};
  const filters = useModuleFilters({ period: 'last_7_days', persistKey: 'lab:atencion' });
  const leadsCtx = useLeadsData();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const forceRef = useRef(false);
  const loadSeq = useRef(0);

  useAnalyticsRefresh(
    useCallback(() => {
      if (!hasSearched) return;
      forceRef.current = true;
      setTick((t) => t + 1);
    }, [hasSearched])
  );

  useEffect(() => {
    if (leadsCtx && leadsCtx.ensureLoaded) leadsCtx.ensureLoaded();
  }, [leadsCtx]);

  useEffect(() => {
    if (!hasSearched) return;
    let cancelled = false;
    const seq = ++loadSeq.current;
    const force = forceRef.current;
    forceRef.current = false;
    const key = cacheKey(filters.range.dateFrom, filters.range.dateTo);
    const hit = !force ? cacheGet(key) : null;
    if (hit && !cancelled) {
      setData(hit.data);
      setLoading(false);
      setError('');
      return undefined;
    }
    setLoading(true);
    setError('');
    (async () => {
      try {
        const payload = await loadExecutiveSummary(
          { dateFrom: filters.range.dateFrom, dateTo: filters.range.dateTo },
          { force }
        );
        if (cancelled || seq !== loadSeq.current) return;
        setData(payload);
        setLoading(false);
      } catch (err) {
        if (cancelled || seq !== loadSeq.current) return;
        setError((err && err.message) || 'No se pudo cargar atención.');
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  function runSearch() {
    setHasSearched(true);
    forceRef.current = true;
    setTick((t) => t + 1);
    if (leadsCtx && leadsCtx.reload) leadsCtx.reload();
  }

  function cancelSearch() {
    loadSeq.current += 1;
    setLoading(false);
    if (leadsCtx && leadsCtx.cancelSearch) leadsCtx.cancelSearch();
  }

  const alerts = (data && data.alerts) || [];
  const attention = (leadsCtx && leadsCtx.data && leadsCtx.data.attention) || null;
  const attentionItems = Array.isArray(attention && attention.items)
    ? attention.items
    : Array.isArray(attention && attention.leads)
      ? attention.leads
      : Array.isArray(attention && attention.list)
        ? attention.list
        : [];
  const attentionRows = attentionItems.map(formatLeadRow).filter(Boolean).slice(0, 25);
  const kpis = (leadsCtx && leadsCtx.data && leadsCtx.data.kpis) || {};

  return (
    <div className="analytics-module lab-module">
      <LabBanner pageLabel="Atención ahora" />
      <PageHeader
        eyebrow="Lab · evaluar UX LeadTrack"
        title="Atención ahora"
        subtitle={
          'Cola accionable con datos GI ya existentes (alertas del Resumen + atención de Leads). ' +
          (email ? `· ${email}` : '')
        }
        actions={
          <ModuleFilters
            filters={filters}
            disabled={false}
            busy={loading}
            onRefresh={runSearch}
            onCancel={cancelSearch}
            showExecutive={false}
            showProject={false}
            showChannel={false}
            showStatus={false}
          />
        }
      />

      {!hasSearched && !loading ? (
        <p className="analytics-empty">
          Elegí el período y pulsá <strong>Buscar</strong>. No se consulta hasta entonces.
        </p>
      ) : null}

      {error ? <ErrorState message={error} /> : null}
      {loading && !data ? <LoadingState label="Buscando alertas…" /> : null}

      <section className="analytics-section">
        <SectionHeader
          title="Qué atender (Resumen ejecutivo)"
          note="Mismas alertas que el Resumen oficial — aquí van primero."
        />
        <div className="analytics-panel">
          <AlertList
            items={alerts.map((a) => ({
              severity:
                a.severity === 'critical' ? 'bad' : a.severity === 'info' ? 'ok' : 'warn',
              title: a.title,
              detail: (a.detail || '') + (a.actionHint ? ' · ' + a.actionHint : '')
            }))}
            emptyLabel="Sin alertas priorizadas en este período (según Resumen oficial)."
          />
          {alerts.some((a) => a.href) ? (
            <p className="exec-alert-links">
              {alerts
                .filter((a) => a.href)
                .map((a) => (
                  <Link key={a.title} to={a.href} className="exec-alert-link">
                    {a.title} →
                  </Link>
                ))}
            </p>
          ) : null}
        </div>
      </section>

      <section className="analytics-section">
        <SectionHeader
          title="Cola de leads (Sales Analytics)"
          note="Misma fuente que /leads — no redefine SLA."
        />
        <div className="analytics-panel">
          <p className="lab-muted">
            Atención total:{' '}
            <strong>
              {attention && attention.total != null
                ? attention.total
                : attentionRows.length || '—'}
            </strong>
            {kpis.slaBreachedFirstContact != null
              ? ` · SLA primer contacto: ${kpis.slaBreachedFirstContact}`
              : ''}
            {' · '}
            <Link to="/">Abrir Leads oficial →</Link>
          </p>
          {leadsCtx && leadsCtx.loading && !leadsCtx.data ? (
            <LoadingState label="Cargando cola de leads…" />
          ) : null}
          {attentionRows.length ? (
            <ul className="lab-attention-list">
              {attentionRows.map((row, i) => (
                <li key={i}>
                  <strong>{row.name}</strong>
                  {row.days != null ? <span className="lab-chip">{row.days}d</span> : null}
                  {row.reason ? <span className="lab-muted"> · {row.reason}</span> : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="analytics-empty">
              Sin ítems en cola de atención con la forma esperada, o aún no cargó Leads.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
